import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMilestones } from '../../actions/project';
import Select from '../../common/Form/Select';
import { useFormContext } from 'react-hook-form';
import Site from './Site';
import Contact from './Contact';
import { actualValueForDropdown, debounce, ScogoID, scogo_repair_center } from '../../utils/common';
import Input from '../../common/Form/Input';
import MultiSelectDatePicker from '../../common/Form/MultiDatePicker';
import Warehouse from './Warehouse';
import scogo_apis from '../../scogo_apis';
import Checkbox from '../../common/Form/Checkbox';
import { openCustomModal } from '../../actions/modal';
import AddAsset from '../../pages/Ticket/Modal/AddAsset';
import Address from './Address';
import Branch from './Branch';
import RenderContact from './Contact';
import RepairCenter from './RepairCenter';

export default function Milestone({ name, label, onChange = () => { }, org_id, required = true, prefill, id, selectedProject, openAddAsset = true, canEditVendorPricing = false, canEditIncentivePricing = false }) {
    const dispatch = useDispatch();
    const methods = useFormContext();
    const milestonesDropdownListData = useSelector((state) => state.project.milestonesDropdown);
    const isMilestonesLoading = useSelector((state) => state.loading.isMilestonesLoading);

    const getMilestoneList = ({ limit = 100, page = 1, query = '', org_id }) => {
        dispatch(getMilestones({ limit, page, query, dropdown: true, payload: { org_id } }));
    };

    const debouncedMilestoneSearchSearch = useCallback(debounce(getMilestoneList), []);

    useEffect(() => {
        if (org_id && (!milestonesDropdownListData || milestonesDropdownListData.length === 0)) {
            getMilestoneList({ limit: 100, page: 1, query: '', org_id });
            methods.setValue(name, null);
        }
    }, [org_id]);

    useEffect(() => {
        if (Array.isArray(milestonesDropdownListData) && prefill) {
            const find = milestonesDropdownListData.find((milestone) => milestone.label === prefill);
            if (find) methods.setValue(name, find);
        }
    }, [milestonesDropdownListData, prefill]);

    const milestoneType = methods.watch(name)?.label;
    const selectedSite = methods.watch(`${name}:site`);
    const isAddNewSite = actualValueForDropdown(selectedSite) === 'add-new-site';

    const openAddAssetModal = () => {
        if (!openAddAsset) return;
        const props = { serial_number: methods.watch(`${name}:serial-number`), org_id, additionalProps: { source: 'create_ticket_form', key: `${name}:serial-number` } };
        dispatch(
            openCustomModal({
                heading: 'Add Asset',
                modalWidth: '95rem',
                modalHeight: 'auto',
                modalComponent: <AddAsset {...props} />,
            })
        );
    };

    const isAddNewDeliveryLocation = actualValueForDropdown(methods.watch(`${name}:delivery_location`)) === 'add-new-site';

    return (
        <>
            <Select
                className='w-full'
                label={label}
                name={name}
                type={'dropdown'}
                options={milestonesDropdownListData}
                isLoading={isMilestonesLoading}
                onInputChange={(event) => {
                    if (event.length > 0 && !milestonesDropdownListData.find(option => option.label.toLowerCase().startsWith(event.toLowerCase()))) {
                        debouncedMilestoneSearchSearch({ query: event })
                    }
                }}
                onChange={(event) => {
                    const value = event?.value;
                    onChange(value, event, id)
                    methods.setValue(`${name}-site`, null)
                }}
                required={required}
            />

            {milestoneType === 'RMA' && (
                <>
                    <Input
                        // required
                        name={`${name}:serial-number`}
                        label="Serial Number"
                        validate={{
                            serialNumberNotInSystem: (value) => {
                                if (value === '') return true;
                                return new Promise(resolve => {
                                    const validateSerialNumber = async () => {
                                        try {
                                            const response = await scogo_apis.inventory.checkAsset({}, {}, {}, { serial_number: value, org_id });

                                            if (response.status === 201 && response.data.data.org_id !== org_id) {
                                                resolve('Serial number belongs to a different organization');
                                                methods.setValue(`${name}:validating`, false);
                                                methods.setValue(`${name}:asset_id`, '');
                                                methods.setValue(`location_id`, '');
                                                return;
                                            }
                                            if (response.status === 201 && response.data.data.id) {
                                                methods.setValue(`${name}:asset_id`, response.data.data.id);
                                                if (response.data.data.location) {
                                                    methods.setValue(`location_id`, {
                                                        label: response.data.data.location.external_id,
                                                        value: response.data.data.location.id,
                                                        key: response.data.data.location.id
                                                    });
                                                    if (response?.data?.data?.location?.address) {
                                                        methods.setValue('location_id:selected_site_address', response?.data?.data?.location?.address);
                                                    } else {
                                                        methods.setValue('location_id:selected_site_address', '');
                                                    }
                                                }
                                                methods.setValue(`${name}:validating`, false);
                                                methods.setValue(`${name}:asset_not_found`, false);
                                                resolve(true);
                                            } else {
                                                methods.setValue(`${name}:validating`, false);
                                                methods.setValue(`${name}:asset_not_found`, true);
                                                methods.setValue(`${name}:asset_id`, '');
                                                resolve("Serial Number doesn't exist in the System.");
                                                methods.setValue(`location_id`, '');
                                            }
                                        } catch (error) {
                                            console.error("Error checking serial number:", error);
                                            methods.setValue(`${name}:validating`, false);
                                            methods.setValue(`${name}:asset_not_found`, true);
                                            methods.setValue(`location_id`, '');
                                            methods.setValue(`${name}:asset_id`, '');
                                            resolve(error?.response?.data?.messages?.[0] || 'Error checking serial number');
                                        }
                                    };

                                    try {
                                        methods.setValue(`${name}:validating`, true);
                                        methods.setValue(`${name}:asset_not_found`, false);
                                        if (!window.debouncedSerialValidation) {
                                            window.debouncedSerialValidation = debounce((callback) => {
                                                callback();
                                            });
                                        }
                                        window.debouncedSerialValidation(validateSerialNumber);
                                    } catch (error) {
                                        console.error("Error in validation:", error);
                                        methods.setValue(`${name}:validating`, false);
                                        methods.setValue(`${name}:asset_not_found`, false);
                                        resolve(error?.response?.data?.messages?.[0] || 'Error validating serial number. Please try again.');
                                    }
                                });
                            },
                        }}
                        isLoading={methods.watch(`${name}:validating`)}
                        iconRight={openAddAsset && methods.watch(`${name}:asset_not_found`) ? { name: 'add', hoverTitle: 'Add Asset', onClick: openAddAssetModal } : null}
                    />
                </>
            )}
            {milestoneType === 'RMA' && <Warehouse addNew={false} required name={`${name}:dispatch_location_id`} label="Warehouse" org_id={org_id} />}
            {milestoneType === 'RMA' && <RepairCenter addNew={false} name={`${name}:receive_location_id`} label="Repair Center" org_id={org_id} prefill={selectedProject?.default_helpdesk_id === ScogoID ? [scogo_repair_center] : []} />}
            {milestoneType === 'LOGISTICS' && <Checkbox id={`${name}:is_same_delivery_location`} label='Is the pickup location same as the delivery location?' name={`${name}:is_same_delivery_location`} />}
            {milestoneType === 'LOGISTICS' && !methods.watch(`${name}:is_same_delivery_location`) && <Site name={`${name}:delivery_location`} label="Delivery Location" addNew org_id={org_id} />}

            {isAddNewDeliveryLocation && <Input required name={`${name}:delivery_location:site-external-id`} label="Site ID" />}
            {isAddNewDeliveryLocation && <Branch add_new org_id={org_id} name={`${name}:delivery_location:site-branch-name`} label="Branch Name" showInputIfEmpty />}
            {isAddNewDeliveryLocation && <Address required name={`${name}:delivery_location:site-address`} label="Address" />}
            {isAddNewDeliveryLocation && <Input label='Region' name={`${name}:delivery_location:region`} />}
            {isAddNewDeliveryLocation && <Input label='Zone' name={`${name}:delivery_location:zone`} />}
            {isAddNewDeliveryLocation && <RenderContact name={`${name}:delivery_location:site-contact`} label="Contact" org_id={org_id} />}


            {(milestoneType && canEditVendorPricing) ? <Input step='any' name={`${name}:SP_SERVICE`} label="Partner Reward" type='number' /> : null}
            {(milestoneType && canEditIncentivePricing) ? <Input step='any' name={`${name}:PSP_INCENTIVE`} label="PSP Incentive" type='number' /> : null}
            {(milestoneType && canEditIncentivePricing) ? <Input step='any' name={`${name}:CLUSTER_INCENTIVE`} label="Cluster Incentive" type='number' /> : null}
            {(milestoneType === 'RMA' && canEditIncentivePricing) ? <Input step='any' name={`${name}:WAREHOUSE_OWNER_INCENTIVE`} label="Warehouse Incentive" type='number' /> : null}
        </>
    )
};
